import * as Sentry from '@sentry/react';
import { useLoaderData, useLocation, useRevalidator } from 'react-router';
import { useEffect } from 'react';
import type { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import supabase from '@/utils/supabase';

import OverviewTab from './OverviewTab';
import DetailsTab from './DetailsTab';
import VideosTab from './VideosTab';
import MediaTab from './MediaTab';
import InvoicesTab from './InvoicesTab';

import StatusBadge from '@/components/StatusBadge';
import { getClientStatusDisplay } from '@/utils/status';

import type { Database } from '@/types/database.types';
type Project = Database['public']['Tables']['projects']['Row'];
type Deliverable = Database['public']['Tables']['deliverables']['Row'];
type Upload = Database['public']['Tables']['uploads']['Row'];
export type DeliverableWithIngest = Deliverable & {
  is_client_ingesting: boolean;
};

type LoaderData = {
  project: Project;
  deliverables: DeliverableWithIngest[];
  uploads: Upload[];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const { projectId } = params;
  if (!projectId) throw new Response('Missing Project ID', { status: 400 });

  const [projectResult, deliverableResult, uploadsResult] = await Promise.all([
    supabase.from('projects').select().eq('id', projectId).single(),
    supabase
      .from('deliverables')
      .select('*, is_client_ingesting')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true })
      .overrideTypes<DeliverableWithIngest[], { merge: false }>(),
    supabase
      .from('uploads')
      .select()
      .eq('project_id', projectId)
      .eq('upload_type', 'media')
      .order('created_at', { ascending: false }),
  ]);

  const { data: project, error: projectError } = projectResult;
  const { data: deliverables, error: deliverablesError } = deliverableResult;
  const { data: uploads, error: uploadsError } = uploadsResult;

  if (projectError || !project) {
    throw new Response('Failed to load project', { status: 500 });
  }

  if (deliverablesError || !deliverables) {
    throw new Response('Failed to load deliverables', { status: 500 });
  }

  if (uploadsError || !uploads) {
    throw new Response('Failed to load uploads', { status: 500 });
  }
  return { project, deliverables, uploads };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const id = formData.get('id') as string;
  const status = formData.get('status') as string;
  const intent = formData.get('intent') as string;

  if (intent === 'updateStatus') {
    const ALLOWED_STATUS_VALUES = ['approved', 'editor_revision'];
    if (!ALLOWED_STATUS_VALUES.includes(status)) {
      throw new Response('Invalid status value', { status: 400 });
    }
    const { data, error } = await supabase
      .from('deliverables')
      .update({ status })
      .eq('id', id)
      .select();

    if (error || !data) {
      throw new Response('Failed to update deliverable status', {
        status: 500,
      });
    }

    if (data.length === 0) {
      throw new Response('Deliverable not found', { status: 404 });
    }

    return Response.json(data);
  } else {
    return Response.json({ error: 'Unknown intent' }, { status: 400 });
  }
}

export default function ClientProjectDetails() {
  const { project, deliverables, uploads } = useLoaderData() as LoaderData;
  const location = useLocation();
  const initialTab = location.state?.initialTab ?? 'overview';
  const revalidator = useRevalidator();

  useEffect(() => {
    const channelName = `deliverables_client_changes_${Date.now()}`;

    const setupChannel = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      supabase.realtime.setAuth(session?.access_token ?? null);

      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'deliverables',
          },
          () => {
            revalidator.revalidate();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'ingest_attempts',
          },
          () => {
            revalidator.revalidate();
          }
        )
        .subscribe();
      return channel;
    };

    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | undefined;
    setupChannel().then((ch) => {
      if (cancelled) {
        supabase.removeChannel(ch);
        return;
      }
      channel = ch;
    });
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (
      !project.frame_io_project_id ||
      !project.frame_io_client_folder_id ||
      !project.frame_io_client_review_link_id
    ) {
      (async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          const response = await fetch(
            `/api/client/projects/${project.id}/setup-frameio`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
              },
            }
          );
          if (!response.ok) {
            let body: unknown;
            try {
              body = await response.json();
            } catch {
              body = await response.text().catch(() => '<unreadable>');
            }
            const error = new Error(
              `Frame.io setup request failed: ${response.status} ${response.statusText}`
            );
            Sentry.captureException(error, {
              tags: { component: 'frameio', op: 'setup-trigger' },
              extra: {
                projectId: project.id,
                status: response.status,
                statusText: response.statusText,
                body,
              },
            });
            console.error('frameio setup failed:', response.status, body);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'frameio', op: 'setup-trigger' },
            extra: { projectId: project.id, kind: 'network' },
          });
          console.error('Frame.io setup network error:', error);
        }
      })();
    }
  }, [
    project.id,
    project.frame_io_project_id,
    project.frame_io_client_folder_id,
    project.frame_io_client_review_link_id,
  ]);

  const anyIngesting = deliverables.some((d) => d.is_client_ingesting);

  const { label, colour, pulse } = getClientStatusDisplay(
    anyIngesting ? 'processing' : project.status
  );

  return (
    <>
      <header>
        <div className="flex flex-row gap-4 items-center mb-5">
          <StatusBadge label={label} colour={colour} pulse={pulse} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <h1 className="text-3xl font-bold">{project.project_name}</h1>
        </div>
      </header>
      <div className="grid">
        <div className="max-w-300">
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList variant={'line'} className="mb-4">
              <TabsTrigger value="overview" className="px-6 cursor-pointer">
                Overview
              </TabsTrigger>
              <TabsTrigger value="videos" className="px-6 cursor-pointer">
                Videos
              </TabsTrigger>
              <TabsTrigger value="media" className="px-6 cursor-pointer">
                Media
              </TabsTrigger>
              <TabsTrigger value="details" className="px-6 cursor-pointer">
                Details
              </TabsTrigger>
              <TabsTrigger value="invoices" className="px-6 cursor-pointer">
                Invoices
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>
            <TabsContent value="details">
              <DetailsTab project={project} />
            </TabsContent>
            <TabsContent value="media">
              <MediaTab projectId={project.id} uploads={uploads} />
            </TabsContent>
            <TabsContent value="videos">
              <VideosTab deliverables={deliverables} project={project} />
            </TabsContent>
            <TabsContent value="invoices">
              <InvoicesTab />
            </TabsContent>
          </Tabs>
        </div>
        {/* <div className="col-span-1 sticky top-0 self-start">
          <ChatPanel projectId={project.id} />
        </div> */}
      </div>
    </>
  );
}
